import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Building2, Briefcase, Wallet, Trophy, Gift, Megaphone,
  MessageCircle, ShieldCheck, Settings, ClipboardList, Boxes, Network, Sparkles,
  LogOut, ChevronDown, Search, Bot, FileBarChart, QrCode, ShieldAlert, Package, Bell,
  Upload, FolderOpen, Compass, KeyRound, GraduationCap, UserPlus, Menu, X
} from "lucide-react";
import { useAuth, ROLE_LABELS, isFieldRole, FIELD_HIDDEN_ROUTES } from "../context/AuthContext";
import { JenburktBrand, CLAIMark, Footer } from "./Brand";
import AIAssistant from "./AIAssistant";
import PasswordChangeModal from "./PasswordChangeModal";

const NAV = [
  { group: "Core", items: [
    { to: "/dashboard", label: "Executive Dashboard", icon: LayoutDashboard, roles: ["super_admin","ho_admin","national_manager","zonal_manager","regional_manager","dse"] },
    { to: "/sales", label: "Sales / DSE Dashboard", icon: Briefcase, roles: ["super_admin","ho_admin","national_manager","zonal_manager","regional_manager","dse"] },
    { to: "/hierarchy", label: "Territory & Hierarchy", icon: Network, roles: ["super_admin","ho_admin","national_manager","zonal_manager","regional_manager"] },
  ]},
  { group: "Loyalty Engine", items: [
    { to: "/wallets", label: "Physio Report", icon: Wallet, roles: ["super_admin","ho_admin","national_manager","zonal_manager","regional_manager","dse"] },
    { to: "/milestones", label: "Milestone Engine", icon: Trophy, roles: ["super_admin","ho_admin","national_manager","zonal_manager","regional_manager","dse"] },
    { to: "/referrals", label: "Referral Engine", icon: Users, roles: ["super_admin","ho_admin","national_manager","zonal_manager","regional_manager","dse"] },
  ]},
  { group: "Network", items: [
    { to: "/physios", label: "Physio Master", icon: Briefcase, roles: ["super_admin","ho_admin","national_manager","zonal_manager","regional_manager","dse"] },
    { to: "/products", label: "Product Catalog", icon: FolderOpen, roles: ["super_admin","ho_admin","national_manager"] },
  ]},
  { group: "Engagement", items: [
    { to: "/rewards", label: "Reward Catalog", icon: Gift, roles: ["super_admin","ho_admin","national_manager","zonal_manager","regional_manager","dse"] },
    { to: "/cohorts", label: "Cohort Dashboard", icon: Compass, roles: ["super_admin","ho_admin","national_manager","zonal_manager","regional_manager"] },
    { to: "/campaigns", label: "Campaign Manager", icon: Megaphone, roles: ["super_admin","ho_admin","national_manager","zonal_manager","regional_manager"] },
    { to: "/whatsapp", label: "WhatsApp Studio", icon: MessageCircle, roles: ["super_admin","ho_admin","national_manager","zonal_manager","regional_manager","dse"] },
    { to: "/whatsapp/messages", label: "WhatsApp Messages", icon: Bot, roles: ["super_admin","ho_admin","national_manager"] },
  ]},
  { group: "Operations", items: [
    { to: "/inventory", label: "Inventory & Fulfillment", icon: Boxes, roles: ["super_admin","ho_admin","national_manager","zonal_manager","regional_manager"] },
    { to: "/fulfillment", label: "Fulfillment Lifecycle", icon: Package, roles: ["super_admin","ho_admin","national_manager","zonal_manager","regional_manager"] },
    { to: "/alerts", label: "48-Hour Alerts", icon: Bell, roles: ["super_admin","ho_admin","national_manager","zonal_manager","regional_manager","dse"] },
    { to: "/grievances", label: "Grievance Desk", icon: ClipboardList, roles: ["super_admin","ho_admin","national_manager","zonal_manager","regional_manager","dse"] },
    { to: "/audit", label: "Compliance & Audit", icon: ShieldCheck, roles: ["super_admin","ho_admin","national_manager"] },
  ]},
  { group: "Reports", items: [
    { to: "/reports/transactions", label: "Transaction Report", icon: FileBarChart, roles: ["super_admin","ho_admin","national_manager","zonal_manager","regional_manager"] },
    { to: "/reports/registrations", label: "Registration Report", icon: UserPlus, roles: ["super_admin","ho_admin","national_manager","zonal_manager","regional_manager"] },
    { to: "/reports/samples", label: "Sample Report", icon: FileBarChart, roles: ["super_admin","ho_admin","national_manager","zonal_manager","regional_manager"] },
    { to: "/reports/redemptions", label: "Redemption Tracking", icon: Package, roles: ["super_admin","ho_admin","national_manager","zonal_manager","regional_manager","dse"] },
  ]},
  { group: "Administration", items: [
    { to: "/admin/customer-search", label: "Customer Search", icon: Search, roles: ["super_admin","ho_admin","national_manager","zonal_manager","regional_manager"] },
    { to: "/admin/field-logins", label: "DSE Mapping & Logins", icon: KeyRound, roles: ["super_admin","ho_admin","national_manager"] },
    { to: "/admin/imports", label: "Data Imports", icon: Upload, roles: ["super_admin","ho_admin","national_manager"] },
    { to: "/users", label: "User Management", icon: Users, roles: ["super_admin","ho_admin","national_manager"] },
    { to: "/tenants", label: "Tenant Management", icon: Building2, roles: ["super_admin"] },
    { to: "/config", label: "Configuration", icon: Settings, roles: ["super_admin","ho_admin","national_manager"] },
  ]},
  { group: "Help", items: [
    { to: "/training", label: "Training & Help Center", icon: GraduationCap, roles: ["super_admin","ho_admin","national_manager","zonal_manager","regional_manager","dse"] },
  ]},
];

export default function AppShell() {
  const { user, tenant, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [aiOpen, setAiOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  if (!user) return null;

  const mustChange = !!user.must_change_password;
  const field = isFieldRole(user.role);

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Mobile overlay */}
      {navOpen && (
        <div className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden" onClick={() => setNavOpen(false)} data-testid="nav-overlay" />
      )}

      {/* Sidebar — slide-in drawer on mobile, static on desktop */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-[260px] bg-brand-navy text-slate-300 flex flex-col transform transition-transform duration-200 ${navOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
        data-testid="app-sidebar"
      >
        <div className="px-5 py-5 border-b border-white/5 flex items-center justify-between">
          <JenburktBrand compact light />
          <button className="lg:hidden text-slate-400 hover:text-white" onClick={() => setNavOpen(false)} data-testid="close-nav-btn">
            <X size={18} />
          </button>
        </div>
        <nav className="flex-1 px-3 py-2 overflow-y-auto">
          {NAV.map((g) => {
            const items = g.items.filter(
              (i) => i.roles.includes(user.role) && !(field && FIELD_HIDDEN_ROUTES.includes(i.to))
            );
            if (!items.length) return null;
            return (
              <div key={g.group} className="mb-1">
                <div className="nav-group-label">{g.group}</div>
                {items.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    onClick={() => setNavOpen(false)}
                    className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
                    data-testid={`nav-${it.to.slice(1)}`}
                  >
                    <it.icon size={16} strokeWidth={1.6} />
                    <span>{it.label}</span>
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>
        <div className="px-5 py-4 border-t border-white/5">
          <CLAIMark tone="light" />
          <div className="mt-2 text-[10px] text-slate-500">AI Loyalty Infrastructure</div>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            <button className="lg:hidden text-slate-600 hover:text-slate-900 shrink-0" onClick={() => setNavOpen(true)} data-testid="open-nav-btn" aria-label="Open menu">
              <Menu size={20} />
            </button>
            <div className="tag tag-slate truncate max-w-[45vw] md:max-w-none" data-testid="tenant-pill">
              <Building2 size={12} /> {tenant?.name || "Platform · Cross-tenant"}
            </div>
            <div className="hidden md:flex items-center gap-2 ml-2 text-[12px] text-slate-500">
              <span className="small-caps">Program</span>
              <span className="text-slate-700">CBC</span>
              <span className="text-slate-300">·</span>
              <span className="small-caps">Brand</span>
              <span className="text-slate-700">ZIXA Strong</span>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <div className="relative hidden md:block">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input className="pl-8 pr-3 py-1.5 text-[12px] w-64 bg-slate-50 border-slate-200" placeholder="Search physios, rewards, tickets…" data-testid="topbar-search" />
            </div>
            <button onClick={() => setAiOpen(true)} className="btn-teal flex items-center gap-2 !py-1.5" data-testid="open-ai-assistant">
              <Bot size={14} /> <span className="hidden sm:inline">Ask CBC Assistant</span>
            </button>
            <div className="relative">
              <details className="group">
                <summary className="list-none cursor-pointer flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-slate-100">
                  <div className="h-8 w-8 rounded-full bg-brand-navy text-white flex items-center justify-center text-[12px] font-medium">
                    {user.name?.[0] || "U"}
                  </div>
                  <div className="text-left hidden md:block">
                    <div className="text-[12px] font-medium text-slate-900 leading-none">{user.name}</div>
                    <div className="text-[11px] text-slate-500 leading-tight mt-0.5">{ROLE_LABELS[user.role] || user.role}</div>
                  </div>
                  <ChevronDown size={14} className="text-slate-400" />
                </summary>
                <div className="absolute right-0 mt-2 w-56 surface p-2 shadow-md z-50">
                  <div className="px-2 py-2 border-b border-slate-100 mb-1">
                    <div className="text-[12px] font-medium text-slate-900">{user.email}</div>
                    <div className="text-[11px] text-slate-500">{ROLE_LABELS[user.role]}</div>
                  </div>
                  <button
                    onClick={async () => { await logout(); nav("/login"); }}
                    className="w-full text-left text-[12px] px-2 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2 text-slate-700"
                    data-testid="logout-btn"
                  >
                    <LogOut size={14} /> Sign out
                  </button>
                </div>
              </details>
            </div>
          </div>
        </header>

        {/* Page */}
        <main className="flex-1 overflow-auto">
          <div className="px-4 md:px-6 py-5 md:py-6 max-w-[1600px] mx-auto">
            <Outlet />
          </div>
        </main>

        <Footer />
      </div>

      {aiOpen && <AIAssistant onClose={() => setAiOpen(false)} />}
      {mustChange && <PasswordChangeModal user={user} onDone={async () => { await refresh(); }} />}
    </div>
  );
}
